/**
 * Creating entities, as pure functions: the kinds on offer (presets of
 * component data), a name unique among siblings, a fresh id, where the new
 * entity goes, and the one-step edit that creates and selects it.
 *
 * Ids are chosen here, not by the command, so a journaled `entity.create`
 * replays to the same entity. The presets name components (they are the
 * editor's Place Actors list, like Unreal's), but nothing else in the editor
 * has to know them: the Inspector and Outliner reflect whatever is created.
 */
import { engineMeshPath } from "../../assets/engineAssets";
import type { EngineShape } from "../../assets/engineAssets";
import type { CommandOrigin } from "../../commands/types";
import { mat4Compose, mat4Invert, mat4Multiply, mat4TransformPoint } from "../../core/math/mat4";
import type { Mat4 } from "../../core/math/mat4";
import { QUAT_IDENTITY } from "../../core/math/quat";
import type { Vec3 } from "../../core/math/vec3";
import type { EntityId, EntityRecord } from "../../world/entity";
import type { RequestGroup } from "../selection/selectionEdits";
import { assetLabel } from "../widgets/assetPickerModel";

export interface CreatePreset {
  /** Menu label. */
  readonly label: string;
  /** The new entity's base name, numbered if a sibling has it. */
  readonly name: string;
  /** Component data besides the Transform, which every created entity gets. */
  readonly components: Readonly<Record<string, object>>;
  /** Child entities created with it (a UI Canvas's first Text), without Transforms. */
  readonly children?: readonly {
    readonly name: string;
    readonly components: Readonly<Record<string, object>>;
  }[];
}

/** An engine shape in a Mesh Renderer, as Unreal's Place Actors › Shapes. */
function shapePreset(name: string, shape: EngineShape): CreatePreset {
  return { label: name, name, components: { MeshRenderer: { mesh: engineMeshPath(shape) } } };
}

/** Just a Transform: a group to parent things under (Unity's Create Empty). */
export const EMPTY_PRESET: CreatePreset = { label: "Empty", name: "Empty", components: {} };

export const CREATE_PRESETS: readonly CreatePreset[] = [
  EMPTY_PRESET,
  shapePreset("Box", "box"),
  shapePreset("Sphere", "sphere"),
  shapePreset("Cylinder", "cylinder"),
  shapePreset("Plane", "plane"),
  { label: "Point Light", name: "PointLight", components: { Light: { kind: "point" } } },
  { label: "Camera", name: "Camera", components: { Camera: {} } },
  // Unity's UI › Text makes a Canvas with a Text in it; this does the same.
  {
    label: "UI Canvas",
    name: "Canvas",
    components: { Canvas: {} },
    children: [{ name: "Text", components: { Text: { text: "New Text", style: "title" } } }],
  },
];

/**
 * What placing a model makes: a Mesh Renderer showing it, named after the
 * file as Unreal names placed actors, with an Animation (playing its first
 * clip) when the file has any.
 */
export function assetPreset(asset: {
  readonly path: string;
  readonly clips: readonly string[];
}): CreatePreset {
  const name = assetLabel(asset.path);
  return {
    label: name,
    name,
    components: {
      MeshRenderer: { mesh: asset.path },
      ...(asset.clips.length > 0 && { Animator: {} }),
    },
  };
}

/**
 * `name` while no sibling has it, else numbered like Unreal's labels: `Box`
 * becomes `Box_1`, and a numbered name counts on from its number (`Box_1`
 * becomes `Box_2`), so duplicates of a duplicate don't nest suffixes.
 */
export function uniqueName(name: string, siblings: readonly string[]): string {
  const taken = new Set(siblings);
  if (!taken.has(name)) return name;

  const numbered = /^(.*)_(\d+)$/.exec(name);
  const base = numbered?.[1] ?? name;
  let n = numbered ? Number(numbered[2]) + 1 : 1;
  while (taken.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

/**
 * Where a new entity goes, in world meters: the camera's focus point (what F
 * framed, or where orbiting turns around), snapped to the translate grid when
 * snapping is on, else to the centimeter so no float noise shows.
 */
export function placement(focus: Vec3, gridMeters: number | null): Vec3 {
  const step = gridMeters ?? 0.01;
  // `+ 0` turns -0 into 0 so the Inspector never shows "-0".
  const snap = (n: number) => Number((Math.round(n / step) * step).toFixed(6)) + 0;
  return [snap(focus[0]), snap(focus[1]), snap(focus[2])];
}

export interface Landing {
  /** The new entity's Transform position, relative to its parent. */
  readonly position: Vec3;
  /** Its world matrix there (no rotation or scale of its own). */
  readonly world: Mat4;
}

/**
 * Where a new entity lands for a world point, under a parent whose world
 * matrix is `parentWorld` (null: the top level): the point in the parent's
 * space, snapped there as `placement` snaps, and the world matrix that gives
 * it. The drop and the drag's ghost both use it, so the ghost shows exactly
 * where the drop will put the entity.
 */
export function landing(at: Vec3, parentWorld: Mat4 | null, gridMeters: number | null): Landing {
  const inverse = parentWorld && mat4Invert(parentWorld);
  const local = inverse ? mat4TransformPoint(inverse, at) : at;
  const position = placement(local, gridMeters);

  const own = mat4Compose(position, QUAT_IDENTITY, [1, 1, 1]);
  return { position, world: parentWorld ? mat4Multiply(parentWorld, own) : own };
}

export interface CreateContext {
  readonly entities: ReadonlyMap<EntityId, EntityRecord>;
  readonly parent: EntityId | null;
  /** The Transform position, relative to the parent. */
  readonly position: Vec3;
}

/** Creates a preset under `parent` and selects it, as one undo step. */
export function createEdit(
  preset: CreatePreset,
  { entities, parent, position }: CreateContext,
  origin: CommandOrigin,
): RequestGroup {
  const siblings = parent === null ? rootsOf(entities) : (entities.get(parent)?.children ?? []);
  const name = uniqueName(
    preset.name,
    siblings.map((id) => entities.get(id)?.name ?? ""),
  );
  const taken = new Set<string>(entities.keys());
  const idFor = (base: string) => {
    const fresh = freshId(base, (candidate) => taken.has(candidate));
    taken.add(fresh);
    return fresh;
  };
  const id = idFor(name);
  const children = (preset.children ?? []).map((child) => ({ id: idFor(child.name), ...child }));

  return {
    label: `Create ${name}`,
    requests: [
      {
        type: "entity.create",
        payload: {
          parent,
          entity: {
            id,
            name,
            components: { Transform: { position }, ...preset.components },
            ...(children.length > 0 && { children }),
          },
        },
        origin,
      },
      { type: "selection.set", payload: { ids: [id] }, origin },
    ],
  };
}

/** An id from the name, e.g. `box-1` for `Box_1`, numbered past any taken one. */
export function freshId(name: string, taken: (id: string) => boolean): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "entity";
  if (!taken(base)) return base;
  let n = 1;
  while (taken(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

function rootsOf(entities: ReadonlyMap<EntityId, EntityRecord>): EntityId[] {
  return [...entities.values()].filter((record) => record.parent === null).map((r) => r.id);
}
